import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { logout } from "@/store/slices/authSlice";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useState, useEffect } from "react";

export default function Navigation() {
  const { t } = useTranslation();
  const { token } = useAppSelector((state) => state.auth);
  const { profile } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasUsageFeature = useFeatureFlag("usage");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Apply user's saved language preference when profile loads
  useEffect(() => {
    if (profile?.language) {
      import("@/i18n").then(({ changeLanguage }) => {
        changeLanguage(profile.language as "en" | "fr");
      });
    }
  }, [profile?.language]);

  const handleLogout = () => {
    dispatch(logout());
    // Navigate to login page
    setTimeout(() => {
      navigate("/login");
    }, 100);
  };

  if (!token) {
    return null;
  }

  return (
    <nav className="bg-primary text-white shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Desktop Navigation */}
          <div className="flex items-center space-x-8">
            <Link to="/" className="text-xl font-heavy tracking-wide uppercase">
              StridR
            </Link>

            <div className="hidden lg:flex space-x-6">
              <Link
                to="/activities"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.activities")}
              </Link>
              <Link
                to="/routes"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.routes")}
              </Link>
              <Link
                to="/map"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.map")}
              </Link>
              <Link
                to="/statistics"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.statistics")}
              </Link>
              <Link
                to="/predictor"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.predictor")}
              </Link>
              <Link
                to="/analyser"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.analyser")}
              </Link>
              <Link
                to="/profile"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
              >
                {t("nav.profile")}
              </Link>
              {hasUsageFeature && (
                <Link
                  to="/usage"
                  className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
                >
                  {t("nav.usage")}
                </Link>
              )}
              {hasUsageFeature && (
                <Link
                  to="/admin"
                  className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors"
                >
                  Admin
                </Link>
              )}
            </div>
          </div>

          {/* Desktop User Info and Logout */}
          <div className="hidden lg:flex items-center space-x-4">
            {profile && (
              <span className="text-sm text-gray-300 whitespace-nowrap">
                {profile.name || profile.email}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium uppercase tracking-wide whitespace-nowrap"
            >
              {t("nav.logout")}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-10 h-10 text-white hover:text-secondary transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-700">
            <div className="flex flex-col space-y-3">
              <Link
                to="/activities"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.activities")}
              </Link>
              <Link
                to="/routes"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.routes")}
              </Link>
              <Link
                to="/map"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.map")}
              </Link>
              <Link
                to="/statistics"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.statistics")}
              </Link>
              <Link
                to="/predictor"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.predictor")}
              </Link>
              <Link
                to="/analyser"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.analyser")}
              </Link>
              <Link
                to="/profile"
                className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("nav.profile")}
              </Link>
              {hasUsageFeature && (
                <Link
                  to="/usage"
                  className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t("nav.usage")}
                </Link>
              )}
              {hasUsageFeature && (
                <Link
                  to="/admin"
                  className="text-sm font-medium uppercase tracking-wide hover:text-secondary transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Admin
                </Link>
              )}
              <div className="pt-4 border-t border-gray-700">
                {profile && (
                  <p className="text-sm text-gray-300 mb-3">{profile.name || profile.email}</p>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium uppercase tracking-wide"
                >
                  {t("nav.logout")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
